/**
 * Documents — documentary state, not a suggestion queue.
 *
 * The 1a source's documents screen offers Confirm/Dismiss on LLM equipment
 * suggestions with a confidence score. That is not current behaviour and is
 * deliberately not reproduced: what exists is a registered document, an
 * append-only history of extraction ATTEMPTS against it, and a review surface
 * for the facts a deterministic parse bound.
 *
 * So this screen answers three questions and stops: what is registered, has it
 * been read, and which reading do I want to open.
 *
 * NOTHING IS CONFIRMED HERE. Extraction produces suggestions; a person
 * confirms a fact onto a unit-day they choose, on the review screen.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { modeller, type DocumentSummary, type ExtractionAttempt } from '../api';
import { canWrite } from '../../api/client';
import { PageHeader } from '../shell/Shell';
import { Empty, ErrorState, Loading } from '../shell/states';
import { Note, Tag } from '../shell/provenance';
import './documents.css';

function bytes(n: number): string {
  if (n >= 1_048_576) return `${(n / 1_048_576).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}

function when(iso: string): string {
  return iso.slice(0, 10);
}

/** The attempts against one document, newest first. Opened on demand: the
 *  list endpoint gives the count, this gives the ids. */
function Attempts({ slug, doc }: { slug: string; doc: DocumentSummary }) {
  const [rows, setRows] = useState<ExtractionAttempt[] | null>(null);
  const [err, setErr] = useState<unknown>(null);

  useEffect(() => {
    let live = true;
    modeller.documentExtractions(slug, doc.id)
      .then((r) => { if (live) setRows(r); })
      .catch((e) => { if (live) setErr(e); });
    return () => { live = false; };
  }, [slug, doc.id]);

  if (err) return <ErrorState what="the extraction history" error={err} />;
  if (!rows) return <Loading what="extractions" />;
  if (!rows.length) return <Note>No extraction attempts recorded.</Note>;

  return (
    <ol className="pem-attempts">
      {rows.map((e, i) => (
        <li key={e.id} className="pem-attempt">
          <div className="pem-attempt__id">
            <Link to={`/modeller/productions/${slug}/extractions/${e.id}`}>
              Extraction {e.id}
            </Link>
            {/* The history is the point: an earlier attempt is not replaced,
                and labelling only the newest keeps that visible. */}
            {i === 0 && rows.length > 1 && <Tag tone="identity">Latest</Tag>}
          </div>
          <div className="pem-attempt__meta">
            <span>{when(e.requested_at)}</span>
            <span>{e.parser_version ?? e.method}</span>
            <span>{e.day_count} day{e.day_count === 1 ? '' : 's'}</span>
            <span>{e.fact_count} fact{e.fact_count === 1 ? '' : 's'}</span>
          </div>
          {e.error && <Note>{e.error}</Note>}
        </li>
      ))}
    </ol>
  );
}

function Row({ slug, doc }: { slug: string; doc: DocumentSummary }) {
  const [open, setOpen] = useState(false);
  const read = doc.extraction_count > 0;
  return (
    <li className="pem-doc">
      <div className="pem-doc__main">
        <div className="pem-doc__head">
          <span className="pem-doc__name">{doc.filename}</span>
          <Tag tone="assumed">{doc.kind.replace('_', ' ')}</Tag>
          {/* Registered-but-never-read is a state, not an absence of one. */}
          {read
            ? <Tag tone="confirmed">
                Read {doc.extraction_count}×
              </Tag>
            : <Tag tone="muted">Not yet read</Tag>}
        </div>
        <div className="pem-doc__meta">
          <span>{bytes(doc.byte_size)}</span>
          {doc.page_count != null && <span>{doc.page_count} pages</span>}
          <span>registered {when(doc.uploaded_at)}</span>
          {doc.uploaded_by && <span>by {doc.uploaded_by}</span>}
        </div>
        {/* Identity, adjacent. This value is what every confirmation copies
            as its proof of which exact file was read, so it stays on the row
            rather than behind a disclosure — but it is labelled in words. */}
        <div className="pem-doc__sha" title={doc.sha256}>
          Fingerprint {doc.sha256.slice(0, 16)}…
        </div>
      </div>
      <div className="pem-doc__side">
        {read ? (
          <button type="button" className="pem-doc__toggle"
                  aria-expanded={open} onClick={() => setOpen((v) => !v)}>
            {open ? '− Hide' : '+ Show'} extraction history
          </button>
        ) : (
          <Note>
            {doc.kind === 'call_sheet'
              ? 'Send the file itself to read it. It must be the exact file '
                + 'registered here — a changed copy is refused.'
              : 'Only call sheets can be read automatically so far.'}
          </Note>
        )}
      </div>
      {open && <div className="pem-doc__attempts"><Attempts slug={slug} doc={doc} /></div>}
    </li>
  );
}

/** Rendered only for a session that can actually register one. */
function RegisterButton({ slug }: { slug: string }) {
  if (!canWrite()) return null;
  return (
    <Link className="pem-btn" to={`/modeller/productions/${slug}/documents/new`}>
      Register a document
    </Link>
  );
}

type State =
  | { t: 'loading' } | { t: 'error'; e: unknown } | { t: 'ok'; rows: DocumentSummary[] };

export function Documents() {
  const { slug = '' } = useParams();
  const [state, setState] = useState<State>({ t: 'loading' });

  useEffect(() => {
    let live = true;
    modeller.documents(slug)
      .then((rows) => { if (live) setState({ t: 'ok', rows }); })
      .catch((e) => { if (live) setState({ t: 'error', e }); });
    return () => { live = false; };
  }, [slug]);

  if (state.t === 'loading') return <><PageHeader title="Documents" /><Loading what="documents" /></>;
  if (state.t === 'error') return <><PageHeader title="Documents" /><ErrorState what="documents" error={state.e} /></>;

  const { rows } = state;
  return (
    <>
      <PageHeader
        title="Documents"
        count={`${rows.length} registered`}
        actions={
          <>
            <RegisterButton slug={slug} />
            <Link className="pem-link" to={`/modeller/productions/${slug}`}>← {slug}</Link>
          </>
        }
      />
      {rows.length === 0 ? (
        <Empty
          title="No documents registered"
          body={
            'A document is recorded here by name, size and fingerprint ' +
            'before the file itself is ever sent. Recording it is the first ' +
            'step; reading it is the second; and putting anything it says onto ' +
            'a shooting day is a third that only a person does. ' +
            (canWrite() ? '' : 'Registering one needs an editing key, issued by Equoia.')
          }
          action={<RegisterButton slug={slug} />}
        />
      ) : (
        <ul className="pem-docs">
          {rows.map((d) => <Row key={d.id} slug={slug} doc={d} />)}
        </ul>
      )}
      <div className="pem-detail-foot">
        <Note>
          Reading a document again keeps the earlier reading too — nothing is
          overwritten. Nothing on this screen changes a shooting day.
        </Note>
      </div>
    </>
  );
}
